import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import { Sparkles, RefreshCw, CheckCircle, AlertCircle, Trash2, Power, Loader } from 'lucide-react';

function AdminCareRecommendations() {
    const [rules, setRules] = useState([]);
    const [seeding, setSeeding] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(query(collection(db, 'careRecommendations'), orderBy('priority')));
            setRules(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error('규칙 로드 실패:', e);
        } finally {
            setLoading(false);
        }
    };

    const runSeed = async () => {
        if (!window.confirm('표준 시드 30건을 일괄 입력합니다.\n기존 시드(source=standard-seed)는 삭제 후 재입력됩니다.\n수동으로 추가한 규칙은 영향 없습니다.\n계속할까요?')) return;
        setSeeding(true);
        setResult(null);
        setError(null);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const seed = httpsCallable(fns, 'seedCareRecommendations', { timeout: 120000 });
            const res = await seed();
            setResult(res.data);
            await load();
        } catch (e) {
            console.error('시드 실패:', e);
            setError(e?.message || '시드 실패');
        } finally {
            setSeeding(false);
        }
    };

    const runMergeContent = async () => {
        if (!window.confirm('AI 분석 콘텐츠(언제·무엇·어떻게·왜) 4가지 필드를 기존 시드 룰에 머지합니다.\n룰 자체는 유지되고 텍스트만 추가됩니다.\n계속할까요?')) return;
        setSeeding(true);
        setResult(null);
        setError(null);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const merge = httpsCallable(fns, 'mergeCareRecommendationContent', { timeout: 120000 });
            const res = await merge();
            setResult({ ...res.data, _mode: 'merge' });
            await load();
        } catch (e) {
            console.error('머지 실패:', e);
            setError(e?.message || '머지 실패');
        } finally {
            setSeeding(false);
        }
    };

    const toggleActive = async (rule) => {
        await updateDoc(doc(db, 'careRecommendations', rule.id), { isActive: !rule.isActive });
        load();
    };

    const deleteRule = async (rule) => {
        if (!window.confirm(`"${rule.title}" 규칙을 삭제할까요?`)) return;
        await deleteDoc(doc(db, 'careRecommendations', rule.id));
        load();
    };

    const ageLabel = (r) => {
        const y = (r.ageMin != null || r.ageMax != null)
            ? `만 ${r.ageMin ?? 0}~${r.ageMax ?? '∞'}세` : '';
        const m = (r.ageMonthMin != null || r.ageMonthMax != null)
            ? `${r.ageMonthMin ?? 0}~${r.ageMonthMax ?? '∞'}개월` : '';
        return [m, y].filter(Boolean).join(' / ') || '전 연령';
    };

    return (
        <div style={S.container}>
            <div style={S.header}>
                <h1 style={S.title}><Sparkles size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} /> 치료 추천 규칙 관리</h1>
                <p style={S.subtitle}>환자 가족 구성원의 나이·성별에 맞춰 치과 검진·치료를 추천하는 규칙입니다</p>
            </div>

            <div style={S.card}>
                <h2 style={S.cardTitle}>표준 시드 일괄 입력</h2>
                <p style={S.cardDesc}>
                    영유아 검진 5단계 + 학생·청장년·노인·임플란트 등 <strong>30개</strong> 표준 규칙을 한 번에 입력합니다.<br />
                    <span style={{ color: '#64748b' }}>※ 기존 시드(source=standard-seed)는 삭제 후 재입력됩니다. 수동 추가 규칙은 영향 없습니다.</span>
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={runSeed} disabled={seeding} style={S.btn}>
                        {seeding ? <><Loader size={14} className="spin" /> 입력 중...</> : <><RefreshCw size={14} /> 시드 일괄 입력 (전체 재생성)</>}
                    </button>
                    <button onClick={runMergeContent} disabled={seeding} style={{ ...S.btn, background: '#0ea5e9' }}>
                        {seeding ? <><Loader size={14} className="spin" /> 머지 중...</> : <><Sparkles size={14} /> AI 콘텐츠 머지 (언제·무엇·어떻게·왜)</>}
                    </button>
                </div>
                {result && result._mode === 'merge' && (
                    <div style={S.success}>
                        <CheckCircle size={14} /> 콘텐츠 머지 — 적용 {result.updated}개 · 누락 {result.missing}개 · 시드 {result.totalInSeed}개
                    </div>
                )}
                {result && result._mode !== 'merge' && (
                    <div style={S.success}>
                        <CheckCircle size={14} /> 삭제 {result.deleted}개 · 신규 {result.inserted}개
                    </div>
                )}
                {error && (
                    <div style={S.error}>
                        <AlertCircle size={14} /> {error}
                    </div>
                )}
            </div>

            <div style={S.card}>
                <h2 style={S.cardTitle}>등록된 규칙 ({rules.length}건)</h2>
                {loading ? (
                    <p style={{ color: '#94a3b8' }}>로딩 중...</p>
                ) : (
                    <table style={S.table}>
                        <thead>
                            <tr style={S.tableHeader}>
                                <th style={S.th}>활성</th>
                                <th style={S.th}>제목</th>
                                <th style={S.th}>대상</th>
                                <th style={S.th}>성별</th>
                                <th style={S.th}>주기</th>
                                <th style={S.th}>보장</th>
                                <th style={S.th}>혜택</th>
                                <th style={S.th}>출처</th>
                                <th style={S.th}>관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rules.map(r => (
                                <tr key={r.id} style={{ ...S.tr, opacity: r.isActive ? 1 : 0.4 }}>
                                    <td style={S.td}>
                                        <button onClick={() => toggleActive(r)} style={S.iconBtn}>
                                            <Power size={14} color={r.isActive ? '#10b981' : '#94a3b8'} />
                                        </button>
                                    </td>
                                    <td style={{ ...S.td, fontWeight: 700 }}>{r.title}</td>
                                    <td style={S.td}>{ageLabel(r)}</td>
                                    <td style={S.td}>{r.gender === 'male' ? '남' : r.gender === 'female' ? '여' : '-'}</td>
                                    <td style={S.td}>{r.cycle || '-'}</td>
                                    <td style={S.td}>{r.coverage || '-'}</td>
                                    <td style={{ ...S.td, fontSize: 11, color: '#64748b' }}>{r.benefit || '-'}</td>
                                    <td style={{ ...S.td, fontSize: 11, color: '#94a3b8' }}>{r.source || '수동'}</td>
                                    <td style={S.td}>
                                        <button onClick={() => deleteRule(r)} style={S.iconBtn}>
                                            <Trash2 size={14} color="#ef4444" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

const S = {
    container: { padding: 24, maxWidth: 1400, margin: '0 auto', fontFamily: "'Apple SD Gothic Neo', sans-serif" },
    header: { marginBottom: 24 },
    title: { fontSize: 24, fontWeight: 900, color: '#0f172a', margin: 0 },
    subtitle: { fontSize: 14, color: '#64748b', marginTop: 4 },
    card: { background: '#fff', borderRadius: 14, padding: 20, marginBottom: 20, border: '1px solid #e2e8f0' },
    cardTitle: { fontSize: 16, fontWeight: 800, marginBottom: 8 },
    cardDesc: { fontSize: 13, color: '#475569', lineHeight: 1.7, marginBottom: 12 },
    btn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
    success: { marginTop: 12, padding: 10, background: '#d1fae5', borderRadius: 8, fontSize: 13, color: '#065f46', display: 'flex', alignItems: 'center', gap: 6 },
    error: { marginTop: 12, padding: 10, background: '#fee2e2', borderRadius: 8, fontSize: 13, color: '#991b1b', display: 'flex', alignItems: 'center', gap: 6 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
    tableHeader: { background: '#f8fafc' },
    th: { padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontSize: 11, fontWeight: 800, color: '#475569' },
    tr: { borderBottom: '1px solid #f1f5f9' },
    td: { padding: '10px 8px' },
    iconBtn: { background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 },
};

export default AdminCareRecommendations;
